import React from 'react';
import { Card, Badge, DataTable } from '@/components/ui';
import { Users, AlertTriangle, Clock, ExternalLink, GitBranch } from 'lucide-react';
import type { Column } from '@/types';
import { formatPercent } from '@/utils/formatters';

interface CoordinationPoint {
  id: string;
  type: 'dependency' | 'shared_epic' | 'resource_conflict' | 'timeline_conflict';
  title: string;
  description: string;
  relatedProgram: {
    id: string;
    name: string;
  };
  severity: 'low' | 'medium' | 'high' | 'critical';
  status: 'open' | 'in_progress' | 'resolved';
  dueDate?: string;
  assignedTo?: string;
}

interface CrossProgramCoordination {
  coordinationPoints: CoordinationPoint[];
  totalCoordinationPoints: number;
  criticalPoints: number;
  resolvedPoints: number;
  programsInvolved: Array<{
    programId: string;
    programName: string;
    coordinationCount: number;
  }>;
}

interface CrossProgramCoordinationViewProps {
  crossProgramCoordination: CrossProgramCoordination | null;
  isLoading: boolean;
}

const CrossProgramCoordinationView: React.FC<CrossProgramCoordinationViewProps> = ({
  crossProgramCoordination,
  isLoading,
}) => {
  const getTypeBadge = (type: string) => {
    const typeConfig = {
      dependency: { variant: 'warning' as const, label: 'Dependency', icon: GitBranch },
      shared_epic: { variant: 'info' as const, label: 'Shared EPIC', icon: Users },
      resource_conflict: { variant: 'danger' as const, label: 'Resource Conflict', icon: AlertTriangle },
      timeline_conflict: { variant: 'warning' as const, label: 'Timeline Conflict', icon: Clock },
    };
    
    const config = typeConfig[type as keyof typeof typeConfig] || typeConfig.dependency;
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const getSeverityBadge = (severity: string) => {
    const severityConfig = {
      critical: { variant: 'danger' as const, label: 'Critical' },
      high: { variant: 'warning' as const, label: 'High' },
      medium: { variant: 'info' as const, label: 'Medium' },
      low: { variant: 'secondary' as const, label: 'Low' },
    };
    
    const config = severityConfig[severity as keyof typeof severityConfig] || severityConfig.medium;
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      open: { variant: 'danger' as const, label: 'Open' },
      in_progress: { variant: 'warning' as const, label: 'In Progress' },
      resolved: { variant: 'success' as const, label: 'Resolved' },
    };
    
    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.open;
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const isOverdue = (dueDate?: string) => {
    if (!dueDate) return false;
    return new Date(dueDate) < new Date();
  };

  const coordinationColumns: Column<CoordinationPoint>[] = [
    {
      key: 'type',
      header: 'Type',
      render: (value) => getTypeBadge(value),
    },
    {
      key: 'title',
      header: 'Coordination Point',
      sortable: true,
      render: (value, row) => (
        <div>
          <div className="font-medium text-foreground">{value}</div>
          <div className="text-sm text-muted-foreground mt-1">{row.description}</div>
        </div>
      ),
    },
    {
      key: 'relatedProgram',
      header: 'Related Program',
      render: (value) => (
        <span className="text-foreground">{value.name}</span>
      ),
    },
    {
      key: 'severity',
      header: 'Severity',
      filterable: true,
      render: (value) => getSeverityBadge(value),
    },
    {
      key: 'status',
      header: 'Status',
      filterable: true,
      render: (value) => getStatusBadge(value),
    },
    {
      key: 'dueDate',
      header: 'Due Date',
      sortable: true,
      render: (value) => {
        if (!value) return <span className="text-muted-foreground">No due date</span>;
        
        const overdue = isOverdue(value);
        return (
          <div className={overdue ? 'text-status-error' : 'text-foreground'}>
            <div>{new Date(value).toLocaleDateString()}</div>
            {overdue && (
              <div className="text-xs text-status-error">Overdue</div>
            )}
          </div>
        );
      },
    },
  ];

  if (isLoading) {
    return (
      <Card>
        <div className="p-6 animate-pulse">
          <div className="h-6 bg-muted/50 rounded w-1/2 mb-4"></div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-20 bg-muted/50 rounded"></div>
            ))}
          </div>
          <div className="h-64 bg-muted/50 rounded"></div>
        </div>
      </Card>
    );
  }

  if (!crossProgramCoordination) {
    return (
      <Card>
        <div className="p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4">Cross-Program Coordination</h3>
          <div className="text-center text-muted-foreground py-8">
            No cross-program coordination data available
          </div>
        </div>
      </Card>
    );
  }

  const activePoints = crossProgramCoordination.coordinationPoints.filter(p => p.status !== 'resolved');
  const overduePoints = crossProgramCoordination.coordinationPoints.filter(p => isOverdue(p.dueDate));

  return (
    <Card>
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-foreground">Cross-Program Coordination</h3>
            <p className="text-sm text-muted-foreground">
              Dependencies and coordination points with other programs
            </p>
          </div>
          <div className="flex items-center space-x-2">
            {crossProgramCoordination.criticalPoints > 0 && (
              <Badge variant="danger">
                {crossProgramCoordination.criticalPoints} Critical
              </Badge>
            )}
            <Badge variant="info">
              {crossProgramCoordination.totalCoordinationPoints} Total
            </Badge>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
          <div className="bg-muted rounded-lg p-4">
            <div className="flex items-center space-x-3 mb-2">
              <Users className="w-5 h-5 text-accent-blue" />
              <span className="font-medium text-foreground">Total Points</span>
            </div>
            <div className="text-2xl font-bold text-foreground">
              {crossProgramCoordination.totalCoordinationPoints}
            </div>
            <div className="text-xs text-muted-foreground">
              {activePoints.length} active
            </div>
          </div>

          <div className="bg-muted rounded-lg p-4">
            <div className="flex items-center space-x-3 mb-2">
              <AlertTriangle className="w-5 h-5 text-status-error" />
              <span className="font-medium text-foreground">Critical</span>
            </div>
            <div className="text-2xl font-bold text-status-error">
              {crossProgramCoordination.criticalPoints}
            </div>
            <div className="text-xs text-muted-foreground">
              Need immediate attention
            </div>
          </div>

          <div className="bg-muted rounded-lg p-4">
            <div className="flex items-center space-x-3 mb-2">
              <Clock className="w-5 h-5 text-status-warning" />
              <span className="font-medium text-foreground">Overdue</span>
            </div>
            <div className="text-2xl font-bold text-status-warning">
              {overduePoints.length}
            </div>
            <div className="text-xs text-muted-foreground">
              Past due date
            </div>
          </div>

          <div className="bg-muted rounded-lg p-4">
            <div className="flex items-center space-x-3 mb-2">
              <GitBranch className="w-5 h-5 text-status-success" />
              <span className="font-medium text-foreground">Resolved</span>
            </div>
            <div className="text-2xl font-bold text-status-success">
              {crossProgramCoordination.resolvedPoints}
            </div>
            <div className="text-xs text-muted-foreground">
              Successfully completed
            </div>
          </div>
        </div>

        {/* Programs Involved */}
        <div className="mb-6">
          <h4 className="font-medium text-foreground mb-4">Programs Involved</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {crossProgramCoordination.programsInvolved.map((program) => (
              <div key={program.programId} className="bg-muted rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium text-foreground">{program.programName}</div>
                    <div className="text-sm text-muted-foreground">
                      {program.coordinationCount} coordination points
                    </div>
                  </div>
                  <button className="text-accent-blue hover:text-blue-300" aria-label="View program details">
                    <ExternalLink className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Coordination Points Table */}
        <div>
          <h4 className="font-medium text-foreground mb-4">Active Coordination Points</h4>
          <DataTable
            data={activePoints}
            columns={coordinationColumns}
            loading={isLoading}
            searchPlaceholder="Search coordination points..."
            emptyMessage="No active coordination points. Great job on cross-program alignment!"
            pageSize={10}
          />
        </div>

        {/* Coordination Insights */}
        <div className="mt-6 pt-6 border-t border-border">
          <h4 className="font-medium text-foreground mb-4">Coordination Insights</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-muted rounded-lg p-4">
              <h5 className="text-sm font-medium text-foreground mb-2">Coordination Load</h5>
              <p className="text-xs text-muted-foreground">
                You have {activePoints.length} active coordination points across {' '}
                {crossProgramCoordination.programsInvolved.length} programs. 
                {crossProgramCoordination.criticalPoints > 0 && 
                  ` ${crossProgramCoordination.criticalPoints} require immediate attention.`
                }
              </p>
            </div>
            
            <div className="bg-muted rounded-lg p-4">
              <h5 className="text-sm font-medium text-foreground mb-2">Success Rate</h5>
              <p className="text-xs text-muted-foreground">
                {formatPercent(crossProgramCoordination.totalCoordinationPoints > 0 ?
                  (crossProgramCoordination.resolvedPoints / crossProgramCoordination.totalCoordinationPoints) * 100 : 0, { decimals: 0 })}
                {' '}of coordination points have been successfully resolved.
                {overduePoints.length > 0 && ` ${overduePoints.length} points are overdue.`}
              </p>
            </div>
          </div>

          {/* Action Items */}
          {(crossProgramCoordination.criticalPoints > 0 || overduePoints.length > 0) && (
            <div className="mt-4 p-3 bg-status-error/10 border border-status-error/30 rounded-lg">
              <div className="flex items-center space-x-2 mb-2">
                <AlertTriangle className="w-4 h-4 text-status-error" />
                <span className="text-sm font-medium text-status-error">Action Required</span>
              </div>
              <div className="text-xs text-foreground space-y-1">
                {crossProgramCoordination.criticalPoints > 0 && (
                  <p>• {crossProgramCoordination.criticalPoints} critical coordination points need immediate attention</p>
                )}
                {overduePoints.length > 0 && (
                  <p>• {overduePoints.length} coordination points are past their due date</p>
                )}
                <p>• Schedule coordination meetings with affected program leads</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
};

export default CrossProgramCoordinationView;